import React, { useState, useEffect } from "react";
import { Dialog } from "@mui/material";
import { IoClose } from "react-icons/io5";

const SlippageSettings = ({ open, setOpen, slippage, setSlippage }) => {
    const [customSlippage, setCustomSlippage] = useState("");
    const [activePreset, setActivePreset] = useState(null);
    const presets = [0.5, 1, 2];

    useEffect(() => {
        // Check if current slippage matches a preset
        if (presets.includes(slippage)) {
            setActivePreset(slippage);
            setCustomSlippage("");
        } else {
            setActivePreset(null);
            setCustomSlippage(slippage.toString());
        }
    }, [slippage]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePresetClick = (preset) => {
        setSlippage(preset);
        setActivePreset(preset);
        setCustomSlippage("");
        // Save to localStorage
        localStorage.setItem("dexora_slippage", preset.toString());
    };

    const handleCustomChange = (value) => {
        // Allow empty string for clearing the input
        if (value === "") {
            setCustomSlippage("");
            return;
        }

        const numValue = parseFloat(value);

        // Only update if it's a valid number within range
        if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
            setCustomSlippage(value);
            setSlippage(numValue);
            setActivePreset(null);
            // Save to localStorage
            localStorage.setItem("dexora_slippage", numValue.toString());
        }
        // If value is out of range, don't update the input field
    };

    return (
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth
            PaperProps={{
                className: "card-3d !rounded-[24px]",
                style: { background: 'none', boxShadow: 'none' }
            }}
        >
            <div className="card-3d p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-5">
                    <h2 className="text-[20px] font-bold gradient-text">Settings</h2>
                    <button
                        onClick={() => setOpen(false)}
                        className="swap-arrow-3d w-9 h-9 flex items-center justify-center"
                    >
                        <IoClose className="text-dexora-text-secondary size-[18px]" />
                    </button>
                </div>

                <h3 className="text-dexora-text-primary text-[15px] font-bold mb-2">
                    Slippage Tolerance
                </h3>
                <p className="text-dexora-text-muted text-[13px] mb-4 leading-relaxed">
                    Your transaction will revert if the price changes unfavorably by more than this percentage.
                </p>

                {/* Preset Buttons */}
                <div className="flex gap-2 mb-4">
                    {presets.map((preset) => (
                        <button
                            key={preset}
                            onClick={() => handlePresetClick(preset)}
                            className={`flex-1 py-3 rounded-xl font-bold text-[15px] transition-all ${
                                activePreset === preset
                                    ? "btn-3d text-dexora-dark"
                                    : "inner-3d text-dexora-text-primary hover:text-dexora-cyan"
                            }`}
                        >
                            {preset}%
                        </button>
                    ))}
                </div>

                {/* Custom Input */}
                <div className="relative">
                    <input
                        type="number"
                        min="0"
                        max="50"
                        step="0.1"
                        value={customSlippage}
                        onChange={(e) => handleCustomChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                e.preventDefault();
                            }
                        }}
                        placeholder="Custom"
                        className="inner-3d w-full py-3 px-4 text-dexora-text-primary focus:border-dexora-cyan/30 outline-none text-[15px] font-semibold"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-dexora-text-muted text-[15px] font-semibold">
                        %
                    </span>
                </div>

                {/* Warning Messages */}
                {slippage > 0 && slippage < 0.5 && (
                    <div className="mt-3 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20" style={{ boxShadow: 'inset 0 2px 4px rgba(234,179,8,0.05)' }}>
                        <p className="text-yellow-500 text-[13px] font-medium">
                            Your transaction may fail due to low slippage
                        </p>
                    </div>
                )}
                {slippage > 5 && (
                    <div className="mt-3 p-3 rounded-xl bg-red-500/10 border border-red-500/20" style={{ boxShadow: 'inset 0 2px 4px rgba(239,68,68,0.05)' }}>
                        <p className="text-red-400 text-[13px] font-medium">
                            High slippage! You may receive less than expected
                        </p>
                    </div>
                )}
                {slippage >= 0.5 && slippage <= 5 && (
                    <div className="mt-3 p-3 rounded-xl bg-green-500/10 border border-green-500/20" style={{ boxShadow: 'inset 0 2px 4px rgba(34,197,94,0.05)' }}>
                        <p className="text-green-400 text-[13px] font-medium">
                            Good slippage tolerance
                        </p>
                    </div>
                )}

                {/* Current Value Display */}
                <div className="mt-4 inner-3d p-3">
                    <p className="text-dexora-text-muted text-[12px] font-medium">
                        Current slippage tolerance:{" "}
                        <span className="text-dexora-cyan font-bold text-[14px]">
                            {slippage}%
                        </span>
                    </p>
                </div>
            </div>
        </Dialog>
    );
};

export default SlippageSettings;
